import { CloseOutlined } from "@ant-design/icons";
import { Dialog, DialogContent } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import "../css/SearchAirportDialog.css";
import Loader from "./Loader";
import useFBOApi from "../../services/api/useFBOApi";

const SearchAirportDialog = ({
  open,
  onClose,
  airportData,
  onSelectAirport,
  disableSearch = false,
}) => {
  const [searchText, setSearchText] = useState("");
  const [searchedAirport, setSearchedAirport] = useState([]);
  const { searchAirport } = useFBOApi();
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Helper function to get airport array from airportData (handles both array and object with data property)
  const getAirportArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data?.data || [];
  };

  // Helper function to sort airports alphabetically
  const sortedAirportList = (airportList) => {
    if (!airportList || !Array.isArray(airportList)) return [];
    const sorted = [...airportList].sort((a, b) => {
      const nameA = (a?.name || "").toLowerCase();
      const nameB = (b?.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
    return sorted;
  };

  const searchAirportMutation = useMutation({
    mutationFn: ({ data }) =>
      searchAirport({
        searchTerm: data,
        limit: 100,
      }),
    onSuccess: (response, variables) => {
      // Only update results if the search term matches the current input
      if (variables.data === searchText) {
        const airportData = response?.data || response || [];
        setSearchedAirport(sortedAirportList(airportData));
      }
    },
  });

  useEffect(() => {
    setSearchText("");
    const airports = getAirportArray(airportData);
    setSearchedAirport(sortedAirportList(airports));

    // Clear any pending search timeout when dialog opens/closes
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }
  }, [open, airportData]);

  const handleAirportSearchChange = (event) => {
    const text = event.target.value;
    setSearchText(text);

    // Clear any existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (text.length > 2) {
      // Set a new timeout to debounce the search
      const timeout = setTimeout(() => {
        searchAirportMutation.mutate({
          data: text,
        });
      }, 300); // 300ms delay
      setSearchTimeout(timeout);
    } else {
      // Reset to initial airport list when search is cleared
      const airports = getAirportArray(airportData);
      setSearchedAirport(sortedAirportList(airports));
    }
  };

  const handleSelectAirport = (airport) => {
    onSelectAirport(airport);
    onClose();
  };

  return (
    <Dialog
      open={open ?? false}
      onClose={onClose}
      PaperProps={{
        className: "search-aircraft-dialog-container",
        sx: { borderRadius: "20px" },
      }}
    >
      <DialogContent>
        <CloseOutlined
          className="search-aircraft-dialog-close"
          onClick={onClose}
        />
        <div className="search-aircraft-dialog-content">
          <div className="search-aircraft-dialog-title">Search Airport</div>
          <input
            placeholder="Search Airport"
            value={searchText}
            onChange={handleAirportSearchChange}
            disabled={disableSearch}
            style={disableSearch ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
          ></input>
          <div className="search-aircraft-dialog-list">
            {searchAirportMutation?.isPending ? (
              <Loader />
            ) : searchedAirport?.length > 0 ? (
              searchedAirport?.map((airport) => (
                <div
                  key={airport?.id}
                  className="search-aircraft-dialog-list-item"
                  onClick={() => handleSelectAirport(airport)}
                >
                  {airport?.name}
                </div>
              ))
            ) : (
              <div className="search-aircraft-dialog-no-results">
                {searchText.length > 2 ? 'No results found' : 'Start typing to search...'}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchAirportDialog;
